import { AppContext, InitialValues } from "../contexts";
import { ReactNode, useEffect, useReducer } from "react";
import { ActionType } from "../types/enums";
import { useAppStore, useCartStore } from "../stores";
import { Cities } from "../utils";
import APIs from "../apis";
import { useAuth } from "../hooks";

const heroImages = (
  hero: {
    images?: { uuid?: string; url?: string; link?: string | null }[];
  } = {},
) =>
  (hero.images || [])
    .map((image) =>
      image?.url
        ? { uuid: image.uuid, url: image.url, link: image.link }
        : undefined,
    )
    .filter(Boolean) as { uuid?: string; url: string; link?: string | null }[];

const reducer = (state: any, action: { type: ActionType; payload?: any }) => {
  switch (action.type) {
    case ActionType.setCart:
      return {
        ...state,
        cart: action.payload,
        cartItems: action.payload.items,
      };
    case ActionType.setAddresses:
      return { ...state, addresses: action.payload };
    case ActionType.setApp:
      return { ...state, ...action.payload };
    case ActionType.setLocation: {
      useCartStore
        .getState()
        .refreshQuote()
        .catch(() => {});
      return { ...state, location: action.payload };
    }
    default:
      return state;
  }
};

const AppProvider = ({ children }: { children: ReactNode }) => {
  const [value, dispatch] = useReducer(reducer, InitialValues.app);
  const refreshQuote = useCartStore((state) => state.refreshQuote);
  const { location, setLocation } = useAppStore();
  const { refreshUser } = useAuth();

  useEffect(() => {
    getApp().finally(() =>
      dispatch({ type: ActionType.setApp, payload: { isReady: true } }),
    );
    refreshUser();
  }, []);

  const getApp = async () => {
    let bootstrap: any = {};
    try {
      bootstrap = await APIs.app.app();
    } catch {
      bootstrap = {};
    }
    let cities = bootstrap.cities ?? [];
    const stored = location;
    if (stored && cities.length) {
      const location = Cities.resolveStoredLocation(cities, stored);
      if (location)
        dispatch({ type: ActionType.setLocation, payload: location });
      else {
        setLocation(undefined);
        refreshQuote().catch(() => {});
      }
    }
    dispatch({
      type: ActionType.setApp,
      payload: {
        bootstrap,
        cities,
        areas: cities.flatMap((city: any) => city.areas ?? []),
        logoUrl: bootstrap?.logoUrl,
        banners: heroImages(bootstrap?.hero),
        payments: bootstrap?.payments ?? [],
        isSiteActive: bootstrap?.site?.isActive ?? true,
        openingHours: bootstrap?.timings ?? [],
        deliveryTime: bootstrap?.meta?.deliveryTime,
        minimumOrder: bootstrap?.meta?.minimumOrder,
      },
    });
  };

  return (
    <AppContext.Provider value={{ ...value, dispatch }} children={children} />
  );
};

export default AppProvider;
